/**
 * HelpTooltip — reusable info-icon tooltip with rich, accessible content.
 * ─────────────────────────────────────────────────────────────────────────────
 * Used across every audit-trail UI to make new features discoverable and
 * self-explanatory. Renders an info icon that on hover/focus displays a
 * titled bubble with body text and optional "Learn more" link.
 *
 * Usage:
 *   <HelpTooltip
 *      title="Activity log"
 *      body="Every change to your shop shows up here — who made it, when, and why."
 *   />
 *
 *   <HelpTooltip title="SLA" body={<>Line 1.<br/>Line 2.</>} />
 *
 * Kept dependency-light (pure CSS hover/focus) so it works anywhere in the SPA.
 *
 * THE BUBBLE IS A PORTAL (31 Oct 2026). It used to render inline, absolutely
 * positioned inside the icon's wrapper, so a tooltip inside anything with
 * `overflow: hidden` or a scrolling table (`.lk-table-frame`, `.table-responsive`,
 * a card) was cut off at the container's edge; on the Tax pages only the arrow
 * showed above a column header. The bubble now renders into document.body with
 * a fixed position measured from the icon, flips below the icon when there is
 * no room above, and stays inside the viewport sideways. The props and the
 * markup inside the bubble are unchanged.
 */

import React, { useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";

// Monotonic counter — React 17 has no useId(); this is sufficient for
// aria-describedby pairing within a single SPA session.
let __helpTooltipCounter = 0;

const baseStyles = {
  wrap: {
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
    marginLeft: 6,
    cursor: "help",
    verticalAlign: "middle",
    lineHeight: 1,
  },
  /* On the console type scale (assets/css/lusuku-overrides.css) rather than a
     one-off rem, so the icon and the bubble match the text they sit beside;
     the hover is the action navy (31 Oct 2026). */
  icon: {
    fontSize: "var(--lk-text-sm, 12px)",
    color: "#6b7a99",
    transition: "color 0.15s ease",
  },
  iconHover: {
    color: "#083860",
  },
  bubble: {
    position: "fixed",
    zIndex: 1080,
    minWidth: 240,
    maxWidth: 340,
    padding: "10px 12px",
    background: "#202a3b",
    color: "#f1f4fa",
    borderRadius: 8,
    fontSize: "var(--lk-text-sm, 12px)",
    lineHeight: 1.45,
    textAlign: "left",
    boxShadow: "0 6px 24px rgba(0,0,0,0.18)",
    pointerEvents: "auto",
  },
  title: {
    fontSize: "var(--lk-text, 13px)",
    fontWeight: 600,
    marginBottom: 4,
    color: "#ffffff",
    letterSpacing: 0.1,
  },
  body: {
    whiteSpace: "normal",
  },
  learnMore: {
    display: "inline-block",
    marginTop: 6,
    color: "#7dbeff",
    fontWeight: 500,
    textDecoration: "underline",
  },
  arrow: {
    position: "absolute",
    top: "100%",
    width: 0,
    height: 0,
    borderLeft: "6px solid transparent",
    borderRight: "6px solid transparent",
    borderTop: "6px solid #202a3b",
  },
  arrowBelow: {
    position: "absolute",
    bottom: "100%",
    width: 0,
    height: 0,
    borderLeft: "6px solid transparent",
    borderRight: "6px solid transparent",
    borderBottom: "6px solid #202a3b",
  },
};

const GAP = 8;
const EDGE = 8;

/**
 * Where the bubble goes, from the icon's rectangle and the bubble's own size.
 * `top` and `bottom` place it above or below the icon, centred, and flip when
 * the preferred side has no room; `left` and `right` place it beside the icon.
 * The result is clamped to the viewport sideways, and the arrow is moved so it
 * still points at the icon after the clamp.
 */
export const positionBubble = (anchor, bubble, placement, viewport) => {
  const vw = viewport.width;
  const vh = viewport.height;
  const centreX = anchor.left + anchor.width / 2;
  const centreY = anchor.top + anchor.height / 2;
  const roomAbove = anchor.top - GAP;
  const roomBelow = vh - anchor.bottom - GAP;

  let side = placement;
  if (side === "top" && roomAbove < bubble.height && roomBelow > roomAbove) side = "bottom";
  if (side === "bottom" && roomBelow < bubble.height && roomAbove > roomBelow) side = "top";

  let left;
  let top;
  if (side === "left" || side === "right") {
    left = side === "right" ? anchor.right + GAP : anchor.left - GAP - bubble.width;
    top = centreY - bubble.height / 2;
  } else {
    left = centreX - bubble.width / 2;
    top = side === "top" ? anchor.top - GAP - bubble.height : anchor.bottom + GAP;
  }
  left = Math.max(EDGE, Math.min(left, vw - EDGE - bubble.width));
  top = Math.max(EDGE, Math.min(top, vh - EDGE - bubble.height));

  /* The arrow points at the icon wherever the clamp moved the bubble. */
  const arrowLeft = Math.max(10, Math.min(centreX - left, bubble.width - 10));
  return { left, top, side, arrowLeft };
};

function HelpTooltip({
  title,
  body,
  text,          // alias for body — many call sites use `text=` instead of `body=`
  learnMoreHref,
  learnMoreText = "Learn more",
  iconClass = "fas fa-info-circle",
  placement = "top", // 'top' | 'right' | 'bottom' | 'left'
  inline = true,     // if false, behaves as block-level (rare)
  ariaLabel,
  children, // alternative: pass rich content directly
}) {
  const resolvedBody = body ?? text; // prefer explicit `body`, fall back to `text`
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [pos, setPos] = useState(null);
  const iconRef = useRef(null);
  const bubbleRef = useRef(null);
  const idRef = useRef(null);
  if (idRef.current == null) idRef.current = `help-tooltip-${++__helpTooltipCounter}`;
  const id = idRef.current;

  /* Measure once the bubble exists, and again on scroll or resize while it is
     open, so a fixed bubble keeps pointing at an icon that moved. */
  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return undefined;
    }
    const place = () => {
      const icon = iconRef.current;
      const bubble = bubbleRef.current;
      if (!icon || !bubble) return;
      setPos(
        positionBubble(
          icon.getBoundingClientRect(),
          bubble.getBoundingClientRect(),
          placement,
          { width: window.innerWidth, height: window.innerHeight },
        ),
      );
    };
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open, placement]);

  const wrapStyle = {
    ...baseStyles.wrap,
    display: inline ? "inline-flex" : "flex",
  };

  /* Invisible until measured, so the first paint is never at (0, 0). */
  const bubbleStyle = pos
    ? { ...baseStyles.bubble, left: pos.left, top: pos.top }
    : { ...baseStyles.bubble, left: 0, top: 0, visibility: "hidden" };
  const side = pos?.side || placement;
  const arrowStyle = side === "top"
    ? { ...baseStyles.arrow, left: pos?.arrowLeft ?? "50%", transform: "translateX(-50%)" }
    : side === "bottom"
      ? { ...baseStyles.arrowBelow, left: pos?.arrowLeft ?? "50%", transform: "translateX(-50%)" }
      : null;

  const bubble = open ? (
    <span ref={bubbleRef} role="tooltip" id={id} style={bubbleStyle} data-placement={side}>
      {title && <div style={baseStyles.title}>{title}</div>}
      <div style={baseStyles.body}>{children || resolvedBody}</div>
      {learnMoreHref && (
        <a
          href={learnMoreHref}
          target="_blank"
          rel="noopener noreferrer"
          style={baseStyles.learnMore}
        >
          {learnMoreText} <i className="fas fa-external-link-alt" style={{ fontSize: "0.7rem" }} />
        </a>
      )}
      {arrowStyle ? <span style={arrowStyle} /> : null}
    </span>
  ) : null;

  return (
    <span
      style={wrapStyle}
      onMouseEnter={() => { setOpen(true); setHovered(true); }}
      onMouseLeave={() => { setOpen(false); setHovered(false); }}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
    >
      <i
        ref={iconRef}
        className={iconClass}
        style={{ ...baseStyles.icon, ...(hovered ? baseStyles.iconHover : {}) }}
        tabIndex={0}
        role="button"
        aria-describedby={open ? id : undefined}
        aria-label={ariaLabel || title || "More information"}
      />
      {bubble && typeof document !== "undefined" ? ReactDOM.createPortal(bubble, document.body) : bubble}
    </span>
  );
}

export default HelpTooltip;
